import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Swal from "sweetalert2";
import Header from "./header";
import { toast } from "react-toastify";
import "moment/locale/th";
import { useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import ApiConfig from "../../common/config/ApiConfig";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Step1 from "./PageComponents/Agent/Edit/Step1";
import Step2 from "./PageComponents/Agent/Edit/Step2";
import Step3 from "./PageComponents/Agent/Edit/Step3";
import Step4 from "./PageComponents/Agent/Edit/Step4";
import { useLanguage } from "../../common/i18n/LanguageContext";

function Limitnumber() {
  const { t } = useLanguage();
  const [reload] = useState(0);
  const [currentComponent, setCurrentComponent] = useState(1);
  const [selectedLotto, setSelectedLotto] = useState("thai");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, Setusername] = useState("Loading");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const usernameurl = searchParams.get("username");
  const positionUrl = searchParams.get("position");
  const uplineusernameUrl = searchParams.get("uplineusername");
  const [name, setName] = useState(searchParams.get("name") ?? "");
  const [tel, setTel] = useState(searchParams.get("tel") ?? "");
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [status, setStatus] = useState(searchParams.get("status") ?? "active");
  const [lock, setLock] = useState(
    ["true", "1"].includes(searchParams.get("lock")),
  );
  const isOwner = localStorage.getItem("position") === "owner";
  // Only an agent sitting directly under the owner may have its currency
  // changed (and only by the owner); anyone lower takes their upline's.
  const [uplineIsOwner, setUplineIsOwner] = useState(false);
  const canChangeCurrency = isOwner && uplineIsOwner;
  const [currencies, setCurrencies] = useState([]);
  const [currencyId, setCurrencyId] = useState(
    Number(searchParams.get("currency_id")) || 1,
  );
  const isDownline = searchParams.get("downline");
  const downlineof = searchParams.get("downlineof");
  const [isThaiTableVisible] = useState(true);

  // Maps LottoType.type (the raw string this page's API calls use, e.g.
  // "th_gov") to the short key each Step component's `show(key)` filter
  // expects — the two vocabularies predate each other and don't line up.
  const LOTTO_TYPE_KEY_BY_TYPE = {
    th_gov: "thai",
    hanoy_PhuQua: "hanoi",
    laos_gov: "laos",
    Yeekee_5: "yeekee",
    Yeekee_10: "yeekeevip",
    Yeekee_15: "yeekeevvip",
  };

  // Maps the short `selectedLotto` key to the raw LottoType.type
  // (buildRateObjFromGroups' first arg) and the field-name prefix used in
  // the /updateagent body (th_maxbet, th_commission, ...) — used by
  // handleUpdate to send only the currently selected lotto type, since
  // /updateagent now supports per-lotto-type partial updates (see
  // docs/updateagent.md).
  const LOTTO_SECTION_BY_KEY = {
    thai: { raw: "th_gov", prefix: "th" },
    hanoi: { raw: "hanoy_PhuQua", prefix: "hanoi" },
    laos: { raw: "laos_gov", prefix: "laos" },
    yeekee: { raw: "Yeekee_5", prefix: "yeekee5" },
    yeekeevip: { raw: "Yeekee_10", prefix: "yeekee10" },
    yeekeevvip: { raw: "Yeekee_15", prefix: "yeekee15" },
  };

  const [lottoTypes, setLottoTypes] = useState([]);
  const getLottoTypes = async () => {
    await ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
  };

  const [packages, setPackages] = useState([]);
  // /getAgentPackageList only returns a username's currently ENABLED
  // packages (status: true) — so the card LIST comes from the UPLINE (only
  // packages the upline itself has can be assigned downward), while each
  // card's switch state comes from a second call scoped to the agent BEING
  // edited (whether THEY currently have that specific package on).
  const getPackages = async () => {
    const uplineAgent = uplineusernameUrl || localStorage.getItem("user");
    const [uplineResult, ownResult] = await Promise.all([
      ApiConfig.get(
        `/getAgentPackageList?username=${encodeURIComponent(uplineAgent)}`,
      ),
      ApiConfig.get(
        `/getAgentPackageList?username=${encodeURIComponent(usernameurl)}`,
      ),
    ]);
    if (uplineResult.resCode !== 200) {
      toast.error(uplineResult.message || t("common_load_error"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    const list = Array.isArray(uplineResult.data) ? uplineResult.data : [];
    setPackages(list);

    const ownEnabledIds = new Set(
      (Array.isArray(ownResult.data) ? ownResult.data : []).map(
        (pkg) => pkg.id,
      ),
    );
    setPackagesData((prev) => {
      const next = { ...prev };
      list.forEach((pkg) => {
        const current = next[pkg.id] ?? DEFAULT_PACKAGE_DATA;
        next[pkg.id] = { ...current, enabled: ownEnabledIds.has(pkg.id) };
      });
      return next;
    });
  };

  // All reward-type groups, every lotto type — fetched once. Step1/Step2's
  // display and the submit payload all derive their per-lotto-type slice
  // from this by filtering on group.lottoType.type, instead of re-fetching
  // per filter change or trusting a hardcoded stake list.
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);
  const getRewardTypeGroups = async () => {
    await ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setRewardTypeGroups(Array.isArray(result.data) ? result.data : []);
    });
  };

  // rewardTypes.code list for a given raw LottoType.type (e.g. "th_gov"),
  // across every group that belongs to it.
  const getRewardTypeCodes = (lottoTypeRaw) =>
    rewardTypeGroups
      .filter((g) => g.lottoType?.type === lottoTypeRaw)
      .flatMap((g) => (g.rewardTypes || []).map((rt) => rt.code));

  // Builds a commission/payrate/maxbet/minbet/maxbetpermatch/maxpercent/
  // fightlimit submit object from the reward-type codes that actually
  // belong to this lotto type, instead of forwarding whatever keys happen
  // to be sitting in state. No table uses an `_mcms` suffix, so read/write
  // key is always the bare code.
  const buildRateObjFromGroups = (lottoTypeRaw, stateObj) => {
    const codes = getRewardTypeCodes(lottoTypeRaw);
    const result = {};
    codes.forEach((code) => {
      result[code] = Number(stateObj[code] ?? 0);
    });
    return result;
  };

  const activeRewardTypeGroups = rewardTypeGroups.filter(
    (g) => LOTTO_TYPE_KEY_BY_TYPE[g.lottoType?.type] === selectedLotto,
  );

  // Step 1: Commission & Payrate — one independent set per package (Step1 is
  // rendered once per package card, toggled open/closed), keyed by package id
  // then by the short lotto key (thai/hanoi/laos/yeekee/yeekeevip/
  // yeekeevvip). *Upline holds this agent's UPLINE's own current rate for
  // that package/lotto (the cap, shown as a hint); the plain values are this
  // agent's own current rate (editable, pre-filled from its existing rows).
  // packagesData[pkgId] = {
  //   enabled: boolean,             // true if this agent already has rows for this package
  //   commission: { thai: {...}, hanoi: {...}, ... },
  //   payrate: { thai: {...}, hanoi: {...}, ... },
  //   commissionUpline: { thai: {...}, ... },
  //   payrateUpline: { thai: {...}, ... },
  // }
  const [packagesData, setPackagesData] = useState({});

  const EMPTY_LOTTO_MAP = {
    thai: {},
    hanoi: {},
    laos: {},
    yeekee: {},
    yeekeevip: {},
    yeekeevvip: {},
  };
  const DEFAULT_PACKAGE_DATA = {
    enabled: false,
    commission: EMPTY_LOTTO_MAP,
    payrate: EMPTY_LOTTO_MAP,
    commissionUpline: EMPTY_LOTTO_MAP,
    payrateUpline: EMPTY_LOTTO_MAP,
  };
  const getPkgData = (pkgId) => packagesData[pkgId] ?? DEFAULT_PACKAGE_DATA;

  const setPackageEnabled = (pkgId, enabled) => {
    setPackagesData((prev) => {
      const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
      return {
        ...prev,
        [pkgId]: { ...current, enabled },
      };
    });
  };

  // Flips the switch immediately via PUT /updateagent/package/status
  // instead of waiting for the full form submit (see
  // docs/updateAgentPackageStatus.md) — optimistic update, reverted if the
  // request fails. A 404 means this package has never been saved for this
  // agent before (no AdminPackage row yet), so there's nothing to flip
  // until the full form is submitted at least once.
  const togglePackageEnabled = async (pkgId) => {
    const nextEnabled = !getPkgData(pkgId).enabled;
    setPackageEnabled(pkgId, nextEnabled);

    try {
      const result = await ApiConfig.put(
        `/updateagent/package/status/${usernameurl}/${pkgId}`,
        { status: nextEnabled },
      );
      if (result.resCode !== 200) {
        setPackageEnabled(pkgId, !nextEnabled);
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch {
      setPackageEnabled(pkgId, !nextEnabled);
      toast.error(t("common_load_error"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Whether a package's card body (Step1) is shown — a separate concern
  // from pkgData.enabled (the on/off switch, which controls the `status`
  // sent to the backend). Accordion behavior: at most one package expanded
  // at a time, defaulting to none (null) — opening one collapses whichever
  // was open before, and clicking the open one again closes it.
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const isPackageExpanded = (pkgId) => expandedPackageId === pkgId;
  const togglePackageExpanded = (pkgId) => {
    setExpandedPackageId((prev) => (prev === pkgId ? null : pkgId));
  };

  // Factory for Step1's setCommission/setPayrateUpdate props, scoped to one
  // package + one lotto key. Step1 calls these both as a plain new object
  // AND as a functional updater `(prev) => next` (its group-sync cascade
  // uses the latter), so this must support both call styles like a normal
  // useState setter does.
  const makePkgFieldSetter = (pkgId, field, lottoKey) => (valueOrFn) => {
    setPackagesData((prev) => {
      const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
      const currentValue = current[field][lottoKey] ?? {};
      const nextValue =
        typeof valueOrFn === "function" ? valueOrFn(currentValue) : valueOrFn;
      return {
        ...prev,
        [pkgId]: {
          ...current,
          [field]: { ...current[field], [lottoKey]: nextValue },
        },
      };
    });
  };

  // Step 2: Bet Limit
  const [Thaimaxbetupdate, setThaimaxbetupdate] = useState({});
  const [Hanoimaxbetupdate, setHanoimaxbetupdate] = useState({});
  const [Laosmaxbetupdate, setLaosmaxbetupdate] = useState({});
  const [Yeekeemaxbetupdate, setYeekeemaxbetupdate] = useState({});
  const [Yeekeevipmaxbetupdate, setYeekeevipmaxbetupdate] = useState({});
  const [Yeekeevvipmaxbetupdate, setYeekeevvipmaxbetupdate] = useState({});
  const [ThaimaxbetUpline, setThaimaxbetUpline] = useState({});
  const [HanoimaxbetUpline, setHanoimaxbetUpline] = useState({});
  const [LaosmaxbetUpline, setLaosmaxbetUpline] = useState({});
  const [YeekeemaxbetUpline, setYeekeemaxbetUpline] = useState({});
  const [YeekeevipmaxbetUpline, setYeekeevipmaxbetUpline] = useState({});
  const [YeekeevvipmaxbetUpline, setYeekeevvipmaxbetUpline] = useState({});
  const [Thaiminbetupdate, setThaiminbetupdate] = useState({});
  const [Hanoiminbetupdate, setHanoiminbetupdate] = useState({});
  const [Laosminbetupdate, setLaosminbetupdate] = useState({});
  const [Yeekeeminbetupdate, setYeekeeminbetupdate] = useState({});
  const [Yeekeevipminbetupdate, setYeekeevipminbetupdate] = useState({});
  const [Yeekeevvipminbetupdate, setYeekeevvipminbetupdate] = useState({});
  const [ThaiminbetUpline, setThaiminbetUpline] = useState({});
  const [HanoiminbetUpline, setHanoiminbetUpline] = useState({});
  const [LaosminbetUpline, setLaosminbetUpline] = useState({});
  const [YeekeeminbetUpline, setYeekeeminbetUpline] = useState({});
  const [YeekeevipminbetUpline, setYeekeevipminbetUpline] = useState({});
  const [YeekeevvipminbetUpline, setYeekeevvipminbetUpline] = useState({});
  const [Thaipermatchupdate, setThaipermatchupdate] = useState({});
  const [Hanoipermatchupdate, setHanoipermatchupdate] = useState({});
  const [Laospermatchupdate, setLaospermatchupdate] = useState({});
  const [Yeekeepermatchupdate, setYeekeepermatchupdate] = useState({});
  const [Yeekeevippermatchupdate, setYeekeevippermatchupdate] = useState({});
  const [Yeekeevvippermatchupdate, setYeekeevvippermatchupdate] = useState({});
  const [ThaipermatchUpline, setThaipermatchUpline] = useState({});
  const [HanoipermatchUpline, setHanoipermatchUpline] = useState({});
  const [LaospermatchUpline, setLaospermatchUpline] = useState({});
  const [YeekeepermatchUpline, setYeekeepermatchUpline] = useState({});
  const [YeekeevippermatchUpline, setYeekeevippermatchUpline] = useState({});
  const [YeekeevvippermatchUpline, setYeekeevvippermatchUpline] = useState({});

  // Step 3: Max Fight %
  const [Thaimaxfightupdate, setThaimaxfightupdate] = useState({});
  const [Hanoimaxfightupdate, setHanoimaxfightupdate] = useState({});
  const [Laosmaxfightupdate, setLaosmaxfightupdate] = useState({});
  const [Yeekeemaxfightupdate, setYeekeemaxfightupdate] = useState({});
  const [Yeekeevipmaxfightupdate, setYeekeevipmaxfightupdate] = useState({});
  const [Yeekeevvipmaxfightupdate, setYeekeevvipmaxfightupdate] = useState({});
  const [ThaimaxfightUpline, setThaimaxfightUpline] = useState({});
  const [HanoimaxfightUpline, setHanoimaxfightUpline] = useState({});
  const [LaosmaxfightUpline, setLaosmaxfightUpline] = useState({});
  const [YeekeemaxfightUpline, setYeekeemaxfightUpline] = useState({});
  const [YeekeevipmaxfightUpline, setYeekeevipmaxfightUpline] = useState({});
  const [YeekeevvipmaxfightUpline, setYeekeevvipmaxfightUpline] = useState({});

  // Step 4: Fight Limit
  const [Thaimaxfightlimitupdate, setThaimaxfightlimitupdate] = useState({});
  const [Hanoimaxfightlimitupdate, setHanoimaxfightlimitupdate] = useState({});
  const [Laosmaxfightlimitupdate, setLaosmaxfightlimitupdate] = useState({});
  const [Yeekeemaxfightlimitupdate, setYeekeemaxfightlimitupdate] = useState(
    {},
  );
  const [Yeekeevipmaxfightlimitupdate, setYeekeevipmaxfightlimitupdate] =
    useState({});
  const [Yeekeevvipmaxfightlimitupdate, setYeekeevvipmaxfightlimitupdate] =
    useState({});
  const [ThaimaxfightlimitUpline, setThaimaxfightlimitUpline] = useState({});
  const [HanoimaxfightlimitUpline, setHanoimaxfightlimitUpline] = useState({});
  const [LaosmaxfightlimitUpline, setLaosmaxfightlimitUpline] = useState({});
  const [YeekeemaxfightlimitUpline, setYeekeemaxfightlimitUpline] = useState(
    {},
  );
  const [YeekeevipmaxfightlimitUpline, setYeekeevipmaxfightlimitUpline] =
    useState({});
  const [YeekeevvipmaxfightlimitUpline, setYeekeevvipmaxfightlimitUpline] =
    useState({});

  const fightpercentandcommissionget = async (pkgId) => {
    const params = new URLSearchParams({
      agent: usernameurl,
      package_id: pkgId,
    });
    ApiConfig.get(`/getfightpercentandcommission?${params.toString()}`)
      .then((result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("common_load_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return {
            commissionArray: [],
            payrateArray: [],
            fightpercentArray: [],
          };
        }
        const list = Array.isArray(result.data) ? result.data : [];
        const commissionArray = list.filter((obj) => "commission" in obj);
        const payrateArray = list.filter((obj) => "payrate" in obj);
        const fightpercentArray = list.filter((obj) => "fightpercent" in obj);
        return { commissionArray, payrateArray, fightpercentArray };
      })
      .then(({ commissionArray, payrateArray, fightpercentArray }) => {
        if (!payrateArray?.[0] || !commissionArray?.[0]) return;

        // Commission/payrate/fight-percent rows are one row per
        // (lotto_type, stake), e.g. { lotto_type: "th_gov", stake: "first",
        // commission: "30.00" }, not one row per lotto_type with named
        // fields on it. *Upline arrays are the editing agent's UPLINE's own
        // rows (the cap); the plain arrays are the agent-being-edited's own
        // current rows for THIS package.
        const cmURows = Array.isArray(commissionArray[0]?.commissionUpline)
          ? commissionArray[0].commissionUpline
          : [];
        const cmORows = Array.isArray(commissionArray[0]?.commission)
          ? commissionArray[0].commission
          : [];
        const getComm = (lottoType, stake, rows) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoType && obj.stake === stake,
          );
          return row ? Number(row.commission) : 0;
        };
        const prURows = Array.isArray(payrateArray[0]?.payrateUpline)
          ? payrateArray[0].payrateUpline
          : [];
        const prORows = Array.isArray(payrateArray[0]?.payrate)
          ? payrateArray[0].payrate
          : [];
        const getPay = (lottoType, stake, rows) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoType && obj.stake === stake,
          );
          return row ? Number(row.payrate) : 0;
        };
        const fpURows = Array.isArray(fightpercentArray[0]?.fightpercentUpline)
          ? fightpercentArray[0].fightpercentUpline
          : [];
        const fpORows = Array.isArray(fightpercentArray[0]?.fightpercent)
          ? fightpercentArray[0].fightpercent
          : [];
        const getFightPercent = (lottoType, stake, rows) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoType && obj.stake === stake,
          );
          return row ? Number(row.fight_percent_limit) : 0;
        };

        const buildFetchedRateObj = (lottoTypeRaw, getter, rows) => {
          const result = {};
          getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
            result[code] = getter(lottoTypeRaw, code, rows);
          });
          return result;
        };

        // Step1's caps and current values are package-scoped — write into
        // this package's own slot in packagesData, not shared top-level
        // state. `enabled` is NOT touched here — it's set once from
        // getPackages()'s /getAgentPackageList call (the real
        // AdminPackage.status), preserved via ...current.
        setPackagesData((prev) => {
          const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
          return {
            ...prev,
            [pkgId]: {
              ...current,
              commission: {
                thai: buildFetchedRateObj("th_gov", getComm, cmORows),
                hanoi: buildFetchedRateObj("hanoy_PhuQua", getComm, cmORows),
                laos: buildFetchedRateObj("laos_gov", getComm, cmORows),
                yeekee: buildFetchedRateObj("Yeekee_5", getComm, cmORows),
                yeekeevip: buildFetchedRateObj("Yeekee_10", getComm, cmORows),
                yeekeevvip: buildFetchedRateObj("Yeekee_15", getComm, cmORows),
              },
              payrate: {
                thai: buildFetchedRateObj("th_gov", getPay, prORows),
                hanoi: buildFetchedRateObj("hanoy_PhuQua", getPay, prORows),
                laos: buildFetchedRateObj("laos_gov", getPay, prORows),
                yeekee: buildFetchedRateObj("Yeekee_5", getPay, prORows),
                yeekeevip: buildFetchedRateObj("Yeekee_10", getPay, prORows),
                yeekeevvip: buildFetchedRateObj("Yeekee_15", getPay, prORows),
              },
              commissionUpline: {
                thai: buildFetchedRateObj("th_gov", getComm, cmURows),
                hanoi: buildFetchedRateObj("hanoy_PhuQua", getComm, cmURows),
                laos: buildFetchedRateObj("laos_gov", getComm, cmURows),
                yeekee: buildFetchedRateObj("Yeekee_5", getComm, cmURows),
                yeekeevip: buildFetchedRateObj("Yeekee_10", getComm, cmURows),
                yeekeevvip: buildFetchedRateObj("Yeekee_15", getComm, cmURows),
              },
              payrateUpline: {
                thai: buildFetchedRateObj("th_gov", getPay, prURows),
                hanoi: buildFetchedRateObj("hanoy_PhuQua", getPay, prURows),
                laos: buildFetchedRateObj("laos_gov", getPay, prURows),
                yeekee: buildFetchedRateObj("Yeekee_5", getPay, prURows),
                yeekeevip: buildFetchedRateObj("Yeekee_10", getPay, prURows),
                yeekeevvip: buildFetchedRateObj("Yeekee_15", getPay, prURows),
              },
            },
          };
        });
        // Step3's fight-percent (own value + upline cap) is NOT
        // package-scoped (FightPercentLimit has no package_id column) —
        // shared top-level state is fine even though this runs once per
        // package (same value every time).
        setThaimaxfightUpline(
          buildFetchedRateObj("th_gov", getFightPercent, fpURows),
        );
        setThaimaxfightupdate(
          buildFetchedRateObj("th_gov", getFightPercent, fpORows),
        );
        setHanoimaxfightUpline(
          buildFetchedRateObj("hanoy_PhuQua", getFightPercent, fpURows),
        );
        setHanoimaxfightupdate(
          buildFetchedRateObj("hanoy_PhuQua", getFightPercent, fpORows),
        );
        setLaosmaxfightUpline(
          buildFetchedRateObj("laos_gov", getFightPercent, fpURows),
        );
        setLaosmaxfightupdate(
          buildFetchedRateObj("laos_gov", getFightPercent, fpORows),
        );
        setYeekeemaxfightUpline(
          buildFetchedRateObj("Yeekee_5", getFightPercent, fpURows),
        );
        setYeekeemaxfightupdate(
          buildFetchedRateObj("Yeekee_5", getFightPercent, fpORows),
        );
        setYeekeevipmaxfightUpline(
          buildFetchedRateObj("Yeekee_10", getFightPercent, fpURows),
        );
        setYeekeevipmaxfightupdate(
          buildFetchedRateObj("Yeekee_10", getFightPercent, fpORows),
        );
        setYeekeevvipmaxfightUpline(
          buildFetchedRateObj("Yeekee_15", getFightPercent, fpURows),
        );
        setYeekeevvipmaxfightupdate(
          buildFetchedRateObj("Yeekee_15", getFightPercent, fpORows),
        );
      });
  };

  const fightlimitget = async () => {
    await ApiConfig.get(
      `/getfightlimit?agent=${encodeURIComponent(usernameurl)}${canChangeCurrency ? `&upline_currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;

      // FightLimit rows are one row per (lotto_type, stake).
      const uplineRows = Array.isArray(result.data?.fightlimtUpline)
        ? result.data.fightlimtUpline
        : [];
      const ownRows = Array.isArray(result.data?.fightlimt)
        ? result.data.fightlimt
        : [];
      const buildObj = (lottoTypeRaw, rows) => {
        const built = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          built[code] = row ? Number(row.fight_limit) : 0;
        });
        return built;
      };

      setThaimaxfightlimitUpline(buildObj("th_gov", uplineRows));
      setThaimaxfightlimitupdate(buildObj("th_gov", ownRows));
      setHanoimaxfightlimitUpline(buildObj("hanoy_PhuQua", uplineRows));
      setHanoimaxfightlimitupdate(buildObj("hanoy_PhuQua", ownRows));
      setLaosmaxfightlimitUpline(buildObj("laos_gov", uplineRows));
      setLaosmaxfightlimitupdate(buildObj("laos_gov", ownRows));
      setYeekeemaxfightlimitUpline(buildObj("Yeekee_5", uplineRows));
      setYeekeemaxfightlimitupdate(buildObj("Yeekee_5", ownRows));
      setYeekeevipmaxfightlimitUpline(buildObj("Yeekee_10", uplineRows));
      setYeekeevipmaxfightlimitupdate(buildObj("Yeekee_10", ownRows));
      setYeekeevvipmaxfightlimitUpline(buildObj("Yeekee_15", uplineRows));
      setYeekeevvipmaxfightlimitupdate(buildObj("Yeekee_15", ownRows));
    });
  };

  const maxbetget = async () => {
    await ApiConfig.get(
      `/Getmaxbet?agentuser=${encodeURIComponent(usernameurl)}${canChangeCurrency ? `&upline_currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MaxBet rows are one row per (lotto_type, stake).
      const uplineRows = Array.isArray(result.data?.maxbetUpline)
        ? result.data.maxbetUpline
        : [];
      const ownRows = Array.isArray(result.data?.maxbet)
        ? result.data.maxbet
        : [];
      const buildObj = (lottoTypeRaw, rows) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.maxbet) : 0;
        });
        return result;
      };

      setThaimaxbetUpline(buildObj("th_gov", uplineRows));
      setThaimaxbetupdate(buildObj("th_gov", ownRows));
      setHanoimaxbetUpline(buildObj("hanoy_PhuQua", uplineRows));
      setHanoimaxbetupdate(buildObj("hanoy_PhuQua", ownRows));
      setLaosmaxbetUpline(buildObj("laos_gov", uplineRows));
      setLaosmaxbetupdate(buildObj("laos_gov", ownRows));
      setYeekeemaxbetUpline(buildObj("Yeekee_5", uplineRows));
      setYeekeemaxbetupdate(buildObj("Yeekee_5", ownRows));
      setYeekeevipmaxbetUpline(buildObj("Yeekee_10", uplineRows));
      setYeekeevipmaxbetupdate(buildObj("Yeekee_10", ownRows));
      setYeekeevvipmaxbetUpline(buildObj("Yeekee_15", uplineRows));
      setYeekeevvipmaxbetupdate(buildObj("Yeekee_15", ownRows));
    });
  };

  const minbetget = async () => {
    await ApiConfig.get(
      `/Getminbet?agentuser=${encodeURIComponent(usernameurl)}${canChangeCurrency ? `&upline_currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MinBet rows are one row per (lotto_type, stake).
      const uplineRows = Array.isArray(result.data?.minbetUpline)
        ? result.data.minbetUpline
        : [];
      const ownRows = Array.isArray(result.data?.minbet)
        ? result.data.minbet
        : [];
      const buildObj = (lottoTypeRaw, rows) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.minbet) : 0;
        });
        return result;
      };

      setThaiminbetUpline(buildObj("th_gov", uplineRows));
      setThaiminbetupdate(buildObj("th_gov", ownRows));
      setHanoiminbetUpline(buildObj("hanoy_PhuQua", uplineRows));
      setHanoiminbetupdate(buildObj("hanoy_PhuQua", ownRows));
      setLaosminbetUpline(buildObj("laos_gov", uplineRows));
      setLaosminbetupdate(buildObj("laos_gov", ownRows));
      setYeekeeminbetUpline(buildObj("Yeekee_5", uplineRows));
      setYeekeeminbetupdate(buildObj("Yeekee_5", ownRows));
      setYeekeevipminbetUpline(buildObj("Yeekee_10", uplineRows));
      setYeekeevipminbetupdate(buildObj("Yeekee_10", ownRows));
      setYeekeevvipminbetUpline(buildObj("Yeekee_15", uplineRows));
      setYeekeevvipminbetupdate(buildObj("Yeekee_15", ownRows));
    });
  };

  const maxpermatchtget = async () => {
    await ApiConfig.get(
      `/Getmaxbetpermatch?agentuser=${encodeURIComponent(usernameurl)}${canChangeCurrency ? `&upline_currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MaxBetPerMatch rows are one row per (lotto_type, stake).
      const uplineRows = Array.isArray(result.data?.maxbetpermatchUpline)
        ? result.data.maxbetpermatchUpline
        : [];
      const ownRows = Array.isArray(result.data?.maxbetpermatch)
        ? result.data.maxbetpermatch
        : [];
      const buildObj = (lottoTypeRaw, rows) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = rows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.maxbetpermatch) : 0;
        });
        return result;
      };

      setThaipermatchUpline(buildObj("th_gov", uplineRows));
      setThaipermatchupdate(buildObj("th_gov", ownRows));
      setHanoipermatchUpline(buildObj("hanoy_PhuQua", uplineRows));
      setHanoipermatchupdate(buildObj("hanoy_PhuQua", ownRows));
      setLaospermatchUpline(buildObj("laos_gov", uplineRows));
      setLaospermatchupdate(buildObj("laos_gov", ownRows));
      setYeekeepermatchUpline(buildObj("Yeekee_5", uplineRows));
      setYeekeepermatchupdate(buildObj("Yeekee_5", ownRows));
      setYeekeevippermatchUpline(buildObj("Yeekee_10", uplineRows));
      setYeekeevippermatchupdate(buildObj("Yeekee_10", ownRows));
      setYeekeevvippermatchUpline(buildObj("Yeekee_15", uplineRows));
      setYeekeevvippermatchupdate(buildObj("Yeekee_15", ownRows));
    });
  };

  useEffect(() => {
    if (uplineusernameUrl) {
      ApiConfig.get(
        `/agentprofile?user=${encodeURIComponent(uplineusernameUrl)}`,
      ).then((result) => {
        setUplineIsOwner(
          result.resCode === 200 && result.data?.position === "owner",
        );
      });
    }
    ApiConfig.get("/getCurrencyList").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setCurrencies(Array.isArray(result.data) ? result.data : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getLottoTypes();
    getRewardTypeGroups();
    getPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fightpercentandcommissionget/fightlimitget/maxbetget/minbetget/
  // maxpermatchtget all build their state from rewardTypeGroups' codes, so
  // they have to wait for that fetch to land — calling them from the mount
  // effect above would close over rewardTypeGroups' still-empty initial
  // value and populate everything as 0.
  useEffect(() => {
    Setusername(usernameurl);
    if (rewardTypeGroups.length > 0) {
      maxbetget();
      fightlimitget();
      minbetget();
      maxpermatchtget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, rewardTypeGroups, currencyId]);

  // Fetch each package's own current values + upline cap once the package
  // list and reward groups are both ready — one call per package (Step1
  // shows every package as its own card, not a single selected one anymore).
  useEffect(() => {
    if (rewardTypeGroups.length > 0 && packages.length > 0) {
      packages.forEach((pkg) => fightpercentandcommissionget(pkg.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardTypeGroups, packages]);

  const handleNext = () => {
    if (currentComponent < 4) setCurrentComponent(currentComponent + 1);
  };

  const handlePrevious = () => {
    if (currentComponent > 1) setCurrentComponent(currentComponent - 1);
  };

  const handleChangeObj = (event, obj, setfunction) => {
    let maxvalue = event.target.value;
    if (maxvalue > 100) maxvalue = 100;
    setfunction({ ...obj, [event.target.name]: Number(maxvalue) });
  };

  const handleChangeFightLimit = (event, obj, setfunction) => {
    let maxvalue = event.target.value;
    if (maxvalue > 10000) maxvalue = 10000;
    setfunction({ ...obj, [event.target.name]: Number(maxvalue) });
  };

  const handleChangepayrateObj = (event, obj, setfunction) => {
    setfunction({ ...obj, [event.target.name]: Number(event.target.value) });
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleUpdate = () => {
    Swal.fire({
      title: t("common_are_you_sure"),
      text: t("agentedit_confirm_text"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("common_yes"),
      cancelButtonText: t("common_no"),
    }).then((result) => {
      if (!result.isConfirmed) return;

      const { raw: activeRaw, prefix: activePrefix } =
        LOTTO_SECTION_BY_KEY[selectedLotto] ?? LOTTO_SECTION_BY_KEY.thai;

      // Only the section for the STEP currently being viewed is sent,
      // scoped to the currently selected lotto type — /updateagent
      // supports per-lotto-type partial updates for every section (see
      // docs/updateagent.md), including `packages[].commissions`/
      // `payrates` themselves being entirely optional, so switching to
      // Step 2/3/4 doesn't require (or risk overwriting with stale state)
      // Step 1's commission/payrate data, and vice versa.
      const json = {
        username: username,
        name: name,
        tel: tel,
        email: email,
        status: status,
        lock: lock,
        ...(canChangeCurrency ? { currency_id: currencyId } : {}),
        // Every package is sent, not just the ones toggled on — a disabled
        // card still upserts its AdminPackage row with status: false, it's
        // just inactive. `id`/`status` go on every entry regardless of
        // step; `commissions`/`payrates` are only attached while Step 1 is
        // open.
        packages: packages.map((pkg) => {
          const pkgData = getPkgData(pkg.id);
          const entry = { id: pkg.id, status: pkgData.enabled };
          if (currentComponent === 1) {
            entry.commissions = {
              [`${activePrefix}_commission`]: buildRateObjFromGroups(
                activeRaw,
                pkgData.commission[selectedLotto],
              ),
            };
            entry.payrates = {
              [`${activePrefix}_payrate`]: buildRateObjFromGroups(
                activeRaw,
                pkgData.payrate[selectedLotto],
              ),
            };
          }
          return entry;
        }),
      };

      if (currentComponent === 2) {
        json[`${activePrefix}_maxbet`] = buildRateObjFromGroups(
          activeRaw,
          activeBetLimit.maxbetUpdate,
        );
        json[`${activePrefix}_minbet`] = buildRateObjFromGroups(
          activeRaw,
          activeBetLimit.minbetUpdate,
        );
        json[`${activePrefix}_maxbetpermatch`] = buildRateObjFromGroups(
          activeRaw,
          activeBetLimit.maxbetpermatchUpdate,
        );
      }
      if (currentComponent === 3) {
        json[`${activePrefix}_maxpercent`] = buildRateObjFromGroups(
          activeRaw,
          activeMaxFight.maxfight,
        );
      }
      if (currentComponent === 4) {
        json[`${activePrefix}_fightlimit`] = buildRateObjFromGroups(
          activeRaw,
          activeFightLimit.fightlimit,
        );
      }

      setIsSubmitting(true);
      ApiConfig.post("/updateagent", json)
        .then((result) => {
          if (result.resCode === 200) {
            toast.success(t("agentedit_save_success"), { autoClose: 2000 });
            packages.forEach((pkg) => fightpercentandcommissionget(pkg.id));
          } else {
            toast.error(result.message || t("agentedit_save_error"), {
              autoClose: 2000,
            });
          }
        })
        .catch(() => {
          toast.error(t("agentedit_save_error"), { autoClose: 2000 });
        })
        .finally(() => setIsSubmitting(false));
    });
  };

  const STEPS = [
    { label: t("agentadd_step_commission_reward"), step: 1 },
    { label: t("agentadd_step_bet_limit"), step: 2 },
    { label: t("agentadd_step_max_fight_percent"), step: 3 },
    { label: t("agentadd_step_fight_limit"), step: 4 },
  ];

  // Step2's min/max/max-per-match bet limits only ever edit the currently
  // selected lotto type (their groups are already scoped to that type
  // below), so resolve which state set is "active" here instead of Step2
  // juggling all six. (Step1's commission/payrate are package-scoped now —
  // see packagesData above — so they don't need this treatment.)
  const ACTIVE_BETLIMIT_BY_KEY = {
    thai: {
      minbetUpdate: Thaiminbetupdate,
      setMinbetUpdate: setThaiminbetupdate,
      minbetUpline: ThaiminbetUpline,
      maxbetUpdate: Thaimaxbetupdate,
      setMaxbetUpdate: setThaimaxbetupdate,
      maxbetUpline: ThaimaxbetUpline,
      maxbetpermatchUpdate: Thaipermatchupdate,
      setMaxbetpermatchUpdate: setThaipermatchupdate,
      maxbetpermatchUpline: ThaipermatchUpline,
    },
    hanoi: {
      minbetUpdate: Hanoiminbetupdate,
      setMinbetUpdate: setHanoiminbetupdate,
      minbetUpline: HanoiminbetUpline,
      maxbetUpdate: Hanoimaxbetupdate,
      setMaxbetUpdate: setHanoimaxbetupdate,
      maxbetUpline: HanoimaxbetUpline,
      maxbetpermatchUpdate: Hanoipermatchupdate,
      setMaxbetpermatchUpdate: setHanoipermatchupdate,
      maxbetpermatchUpline: HanoipermatchUpline,
    },
    laos: {
      minbetUpdate: Laosminbetupdate,
      setMinbetUpdate: setLaosminbetupdate,
      minbetUpline: LaosminbetUpline,
      maxbetUpdate: Laosmaxbetupdate,
      setMaxbetUpdate: setLaosmaxbetupdate,
      maxbetUpline: LaosmaxbetUpline,
      maxbetpermatchUpdate: Laospermatchupdate,
      setMaxbetpermatchUpdate: setLaospermatchupdate,
      maxbetpermatchUpline: LaospermatchUpline,
    },
    yeekee: {
      minbetUpdate: Yeekeeminbetupdate,
      setMinbetUpdate: setYeekeeminbetupdate,
      minbetUpline: YeekeeminbetUpline,
      maxbetUpdate: Yeekeemaxbetupdate,
      setMaxbetUpdate: setYeekeemaxbetupdate,
      maxbetUpline: YeekeemaxbetUpline,
      maxbetpermatchUpdate: Yeekeepermatchupdate,
      setMaxbetpermatchUpdate: setYeekeepermatchupdate,
      maxbetpermatchUpline: YeekeepermatchUpline,
    },
    yeekeevip: {
      minbetUpdate: Yeekeevipminbetupdate,
      setMinbetUpdate: setYeekeevipminbetupdate,
      minbetUpline: YeekeevipminbetUpline,
      maxbetUpdate: Yeekeevipmaxbetupdate,
      setMaxbetUpdate: setYeekeevipmaxbetupdate,
      maxbetUpline: YeekeevipmaxbetUpline,
      maxbetpermatchUpdate: Yeekeevippermatchupdate,
      setMaxbetpermatchUpdate: setYeekeevippermatchupdate,
      maxbetpermatchUpline: YeekeevippermatchUpline,
    },
    yeekeevvip: {
      minbetUpdate: Yeekeevvipminbetupdate,
      setMinbetUpdate: setYeekeevvipminbetupdate,
      minbetUpline: YeekeevvipminbetUpline,
      maxbetUpdate: Yeekeevvipmaxbetupdate,
      setMaxbetUpdate: setYeekeevvipmaxbetupdate,
      maxbetUpline: YeekeevvipmaxbetUpline,
      maxbetpermatchUpdate: Yeekeevvippermatchupdate,
      setMaxbetpermatchUpdate: setYeekeevvippermatchupdate,
      maxbetpermatchUpline: YeekeevvippermatchUpline,
    },
  };
  const activeBetLimit =
    ACTIVE_BETLIMIT_BY_KEY[selectedLotto] ?? ACTIVE_BETLIMIT_BY_KEY.thai;

  // Same idea as ACTIVE_BETLIMIT_BY_KEY above, for Step3's max fight
  // percent.
  const ACTIVE_MAXFIGHT_BY_KEY = {
    thai: {
      maxfight: Thaimaxfightupdate,
      setMaxfight: setThaimaxfightupdate,
      maxfightUpline: ThaimaxfightUpline,
    },
    hanoi: {
      maxfight: Hanoimaxfightupdate,
      setMaxfight: setHanoimaxfightupdate,
      maxfightUpline: HanoimaxfightUpline,
    },
    laos: {
      maxfight: Laosmaxfightupdate,
      setMaxfight: setLaosmaxfightupdate,
      maxfightUpline: LaosmaxfightUpline,
    },
    yeekee: {
      maxfight: Yeekeemaxfightupdate,
      setMaxfight: setYeekeemaxfightupdate,
      maxfightUpline: YeekeemaxfightUpline,
    },
    yeekeevip: {
      maxfight: Yeekeevipmaxfightupdate,
      setMaxfight: setYeekeevipmaxfightupdate,
      maxfightUpline: YeekeevipmaxfightUpline,
    },
    yeekeevvip: {
      maxfight: Yeekeevvipmaxfightupdate,
      setMaxfight: setYeekeevvipmaxfightupdate,
      maxfightUpline: YeekeevvipmaxfightUpline,
    },
  };
  const activeMaxFight =
    ACTIVE_MAXFIGHT_BY_KEY[selectedLotto] ?? ACTIVE_MAXFIGHT_BY_KEY.thai;

  // Same idea as ACTIVE_MAXFIGHT_BY_KEY above, for Step4's fight limit.
  const ACTIVE_FIGHTLIMIT_BY_KEY = {
    thai: {
      fightlimit: Thaimaxfightlimitupdate,
      setFightlimit: setThaimaxfightlimitupdate,
      fightlimitUpline: ThaimaxfightlimitUpline,
    },
    hanoi: {
      fightlimit: Hanoimaxfightlimitupdate,
      setFightlimit: setHanoimaxfightlimitupdate,
      fightlimitUpline: HanoimaxfightlimitUpline,
    },
    laos: {
      fightlimit: Laosmaxfightlimitupdate,
      setFightlimit: setLaosmaxfightlimitupdate,
      fightlimitUpline: LaosmaxfightlimitUpline,
    },
    yeekee: {
      fightlimit: Yeekeemaxfightlimitupdate,
      setFightlimit: setYeekeemaxfightlimitupdate,
      fightlimitUpline: YeekeemaxfightlimitUpline,
    },
    yeekeevip: {
      fightlimit: Yeekeevipmaxfightlimitupdate,
      setFightlimit: setYeekeevipmaxfightlimitupdate,
      fightlimitUpline: YeekeevipmaxfightlimitUpline,
    },
    yeekeevvip: {
      fightlimit: Yeekeevvipmaxfightlimitupdate,
      setFightlimit: setYeekeevvipmaxfightlimitupdate,
      fightlimitUpline: YeekeevvipmaxfightlimitUpline,
    },
  };
  const activeFightLimit =
    ACTIVE_FIGHTLIMIT_BY_KEY[selectedLotto] ?? ACTIVE_FIGHTLIMIT_BY_KEY.thai;

  const pos = localStorage.getItem("position");

  return (
    <div className="h-screen flex flex-col overflow-hidden mb-10">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header
              title={
                isDownline === "true"
                  ? `${t("agentedit_downline_header")} => ${username} (${t("agentedit_downline_header_of")} ${downlineof})`
                  : `${t("agentedit_header_title")} => ${username}`
              }
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="flex items-center justify-center mb-3 mt-2">
              <label className="text-[13px] font-bold mr-2">
                {t("member_commission_filter_label")}
              </label>
              <select
                value={selectedLotto}
                onChange={(e) => setSelectedLotto(e.target.value)}
                className="border-[1px] border-slate-300 rounded-lg text-[13px] font-bold px-2 py-1"
              >
                {lottoTypes
                  .filter((lt) => LOTTO_TYPE_KEY_BY_TYPE[lt.type])
                  .map((lt) => (
                    <option key={lt.id} value={LOTTO_TYPE_KEY_BY_TYPE[lt.type]}>
                      {lt.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="md:w-auto bg-amber-100 p-2 h-auto border-[1px] border-slate-300 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("login_username_label")}
                  </span>
                  <input
                    type="text"
                    value={username}
                    disabled
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-150 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agent_col_position")}
                  </span>
                  <input
                    type="text"
                    value={positionUrl}
                    disabled
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-150 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agent_col_upline")}
                  </span>
                  <input
                    value={uplineusernameUrl}
                    type="text"
                    disabled
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-150 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agentadd_name_label")} ({t("common_optional")})
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agentadd_tel_label")} ({t("common_optional")})
                  </span>
                  <input
                    type="text"
                    value={tel}
                    onChange={(e) => setTel(e.target.value)}
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agentadd_email_label")} ({t("common_optional")})
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agentadd_currency_label")}
                  </span>
                  <select
                    value={currencyId}
                    disabled={!canChangeCurrency}
                    onChange={(e) => setCurrencyId(Number(e.target.value))}
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500 disabled:bg-slate-100"
                  >
                    {currencies
                      .filter((c) => c.status || c.id === currencyId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} - {c.name} ({c.symbol})
                        </option>
                      ))}
                  </select>
                  {canChangeCurrency && (
                    <span className="text-[10px] text-slate-500">
                      {t("agentedit_currency_cascade_hint")}
                    </span>
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold">
                    {t("agentadd_status_label")}
                  </span>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="active">
                      {t("agentadd_status_active")}
                    </option>
                    <option value="inactive">
                      {t("agentadd_status_inactive")}
                    </option>
                    <option value="suspend">
                      {t("agentadd_status_suspend")}
                    </option>
                  </select>
                </div>
                <div className="flex flex-col justify-center">
                  <label className="inline-flex items-center cursor-pointer gap-2 mt-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={lock}
                      onChange={(e) => setLock(e.target.checked)}
                    />
                    <div className="relative w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-red-600 transition-colors">
                      <div
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          lock ? "translate-x-5" : ""
                        }`}
                      />
                    </div>
                    <span className="text-xs font-bold">
                      {t("agentadd_lock_label")}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="stepper flex items-center justify-center mt-3">
              {STEPS.map(({ label, step }, idx, arr) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => setCurrentComponent(step)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                        currentComponent === step
                          ? "bg-brand-blue-600 border-brand-blue-600 text-white"
                          : currentComponent > step
                            ? "bg-brand-blue-300 border-brand-blue-300 text-white"
                            : "bg-white border-gray-300 text-gray-400"
                      }`}
                    >
                      {step}
                    </button>
                    <span
                      className={`text-[11px] mt-1 text-center whitespace-nowrap ${
                        currentComponent === step
                          ? "text-brand-blue-600 font-bold"
                          : currentComponent > step
                            ? "text-brand-blue-400"
                            : "text-gray-400"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < arr.length - 1 && (
                    <div
                      className={`h-[2px] w-12 mb-4 mx-1 transition-colors ${
                        currentComponent > step
                          ? "bg-brand-blue-400"
                          : "bg-gray-300"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            {currentComponent === 1 && (
              <div className="flex flex-col gap-2">
                {packages.map((pkg) => {
                  const pkgData = getPkgData(pkg.id);
                  const pkgName =
                    t(pkg.code) !== pkg.code ? t(pkg.code) : pkg.name;
                  return (
                    <div
                      key={pkg.id}
                      className="w-full border-[1px] border-slate-300 rounded-lg bg-white overflow-hidden"
                    >
                      <div className="flex items-center justify-left px-3 py-2 bg-slate-100">
                        <button
                          type="button"
                          onClick={() => togglePackageExpanded(pkg.id)}
                          className="flex items-center gap-1 text-[14px] font-bold"
                        >
                          {isPackageExpanded(pkg.id) ? (
                            <ExpandLessIcon style={{ fontSize: 20 }} />
                          ) : (
                            <ExpandMoreIcon style={{ fontSize: 20 }} />
                          )}
                          {pkgName}
                        </button>
                        <label className="inline-flex items-center cursor-pointer ml-4">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={pkgData.enabled}
                            onChange={() => togglePackageEnabled(pkg.id)}
                          />
                          <div className="relative w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-600 transition-colors">
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                pkgData.enabled ? "translate-x-5" : ""
                              }`}
                            />
                          </div>
                        </label>
                      </div>
                      {isPackageExpanded(pkg.id) && (
                        <div className="p-2">
                          <Step1
                            isThaiTableVisible={isThaiTableVisible}
                            rewardTypeGroups={activeRewardTypeGroups}
                            payrateUpdate={pkgData.payrate[selectedLotto] ?? {}}
                            setPayrateUpdate={makePkgFieldSetter(
                              pkg.id,
                              "payrate",
                              selectedLotto,
                            )}
                            payrateUpline={
                              pkgData.payrateUpline[selectedLotto] ?? {}
                            }
                            commission={pkgData.commission[selectedLotto] ?? {}}
                            setCommission={makePkgFieldSetter(
                              pkg.id,
                              "commission",
                              selectedLotto,
                            )}
                            commissionUpline={
                              pkgData.commissionUpline[selectedLotto] ?? {}
                            }
                            handleChangeObj={handleChangeObj}
                            handleChangepayrateObj={handleChangepayrateObj}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {currentComponent === 2 && (
              <Step2
                isThaiTableVisible={isThaiTableVisible}
                uplineCurrencySymbol={
                  currencies.find((c) => c.id === currencyId)?.symbol ?? ""
                }
                rewardTypeGroups={activeRewardTypeGroups}
                minbetUpdate={activeBetLimit.minbetUpdate}
                setMinbetUpdate={activeBetLimit.setMinbetUpdate}
                minbetUpline={activeBetLimit.minbetUpline}
                maxbetUpdate={activeBetLimit.maxbetUpdate}
                setMaxbetUpdate={activeBetLimit.setMaxbetUpdate}
                maxbetUpline={activeBetLimit.maxbetUpline}
                maxbetpermatchUpdate={activeBetLimit.maxbetpermatchUpdate}
                setMaxbetpermatchUpdate={activeBetLimit.setMaxbetpermatchUpdate}
                maxbetpermatchUpline={activeBetLimit.maxbetpermatchUpline}
                handleChangepayrateObj={handleChangepayrateObj}
              />
            )}
            {currentComponent === 3 && (
              <Step3
                isThaiTableVisible={isThaiTableVisible}
                rewardTypeGroups={activeRewardTypeGroups}
                maxfight={activeMaxFight.maxfight}
                setMaxfight={activeMaxFight.setMaxfight}
                maxfightUpline={activeMaxFight.maxfightUpline}
                handleChangeObj={handleChangeObj}
              />
            )}
            {currentComponent === 4 && (
              <Step4
                isThaiTableVisible={isThaiTableVisible}
                uplineCurrencySymbol={
                  currencies.find((c) => c.id === currencyId)?.symbol ?? ""
                }
                rewardTypeGroups={activeRewardTypeGroups}
                fightlimit={activeFightLimit.fightlimit}
                setFightlimit={activeFightLimit.setFightlimit}
                fightlimitUpline={activeFightLimit.fightlimitUpline}
                handleChangeObj={handleChangeFightLimit}
              />
            )}
            <div className="flex flex-row space-x-2 mb-1">
              {currentComponent < 4 && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="w-[100%] h-[2rem] bg-brand-blue-600 mr-1 hover:bg-brand-blue-700 text-white text-[14px] font-bold py-1 px-2 mr-1 rounded flex items-center justify-center gap-1"
                >
                  {t("common_next")}{" "}
                  <ArrowForwardIosIcon style={{ fontSize: 20 }} />
                </button>
              )}
              {currentComponent !== 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="w-[100%] h-[2rem] bg-brand-blue-600 mr-1 hover:bg-brand-blue-700 text-white text-[14px] font-bold py-1 px-2 mr-1 rounded flex items-center justify-center gap-1"
                >
                  <ArrowBackIosNewIcon style={{ fontSize: 20 }} />{" "}
                  {t("common_previous")}
                </button>
              )}
              <button
                type="button"
                onClick={handleUpdate}
                disabled={isSubmitting}
                className="w-[100%] h-[2rem] bg-green-600 mr-1 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-bold py-1 px-2 mr-1 rounded flex items-center justify-center gap-1"
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={16} sx={{ color: "white" }} />
                    {t("common_saving")}
                  </>
                ) : (
                  <>
                    <SaveAltIcon style={{ fontSize: 20 }} />{" "}
                    {t("membertable_save")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="w-[100%] h-[2rem] bg-slate-400 hover:bg-slate-500 text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
              >
                <CancelIcon style={{ fontSize: 20 }} /> {t("common_cancel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Limitnumber;
